import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { db, storage } from '../../auth/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { FaPlus, FaTrash } from 'react-icons/fa';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import './chapter_notes.css';
import cacheManager from '../../auth/cache_manager';

// # Import step icons
import stepTwoIcon from '../../aneo_media/mentee/two.png';

// # Import icons for chapters
import anatomyIcon from '../library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../library/step1/step1_icons/blood.png';
import cnsIcon from '../library/step1/step1_icons/cns.png';
import earIcon from '../library/step1/step1_icons/ear.png';
import emergencyIcon from '../library/step1/step1_icons/emergency.png';
import endocrineIcon from '../library/step1/step1_icons/endocrine.png';
import eyeIcon from '../library/step1/step1_icons/eye.png';
import gitIcon from '../library/step1/step1_icons/git.png';
import healthcareIcon from '../library/step1/step1_icons/healthcare.png';
import heartIcon from '../library/step1/step1_icons/heart.png';
import immunologyIcon from '../library/step1/step1_icons/immunology.png';
import infectiousIcon from '../library/step1/step1_icons/infectious.png';
import kidneyIcon from '../library/step1/step1_icons/kidney.png';
import lungsIcon from '../library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../library/step1/step1_icons/newborn.png';
import pathologyIcon from '../library/step1/step1_icons/pathology.png';
import pharmaIcon from '../library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../library/step1/step1_icons/public_health.png';
import qualityIcon from '../library/step1/step1_icons/quality.png';
import reproductiveIcon from '../library/step1/step1_icons/reproductive-system.png';
import revisionIcon from '../library/step1/step1_icons/revision.png';
import skinIcon from '../library/step1/step1_icons/skin.png';
import statisticsIcon from '../library/step1/step1_icons/statistics.png';
import surgeryIcon from '../library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../library/step1/step1_icons/well_patient.png';

function ChapterNotes() {
  const { stepId, chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chapterIcon, setChapterIcon] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmNoteId, setConfirmNoteId] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };
  
  // # Handle return to notes
  const handleReturn = () => {
    // # Always return to notes page, not dashboard
    const isAdmin = window.location.pathname.startsWith('/admin');
    navigate(isAdmin ? '/admin/notes' : '/notes');
  };
  
  // # Format chapter ID consistently with library and my_notes files
  const formatChapterId = (id) => {
    // # We receive chapterId from the URL which is already formatted
    // # This is used for consistency when creating additional URLs
    return id;
  };
  
  // # Handle create new note
  const handleCreateNote = () => {
    const isAdmin = window.location.pathname.startsWith('/admin');
    const basePath = isAdmin ? '/admin/notes' : '/notes';
    const formattedChapterId = formatChapterId(chapterId);
    navigate(`${basePath}/${stepId}/${formattedChapterId}/create`);
  };
  
  // # Show confirmation dialog
  const showDeleteConfirmation = (noteId) => {
    setConfirmNoteId(noteId);
    setShowConfirmDialog(true);
  };
  
  // # Handle cancel delete
  const handleCancelDelete = () => {
    setConfirmNoteId(null);
    setShowConfirmDialog(false);
  };
  
  // # Handle delete note
  const handleDeleteNote = async (noteId) => {
    if (!user || !noteId || deleting) return;
    
    // # Close the confirmation dialog first
    setShowConfirmDialog(false);
    
    try {
      setDeleting(true);
      setDeleteNoteId(noteId);
      
      // # Format keys for Firestore - make sure to replace hyphens with underscores
      const stepKey = `${stepId}_notes`.replace(/[\s-]+/g, '_').toLowerCase();
      const chapterKey = normalizeChapterId(chapterId);
      
      // # Get the note data to check for media
      const notesRef = doc(db, 'notes', user.uid);
      const notesDoc = await getDoc(notesRef);
      
      if (notesDoc.exists()) {
        const notesData = notesDoc.data();
        
        // # Check if the note exists
        if (notesData[stepKey] && 
            notesData[stepKey][chapterKey] && 
            notesData[stepKey][chapterKey][noteId]) {
          
          const noteToDelete = notesData[stepKey][chapterKey][noteId];
          let mediaDeleteErrors = 0;
          
          // # Delete associated media from storage if it exists
          if (noteToDelete.media && Array.isArray(noteToDelete.media)) {
            // # Process each media item (images need to be deleted from storage)
            for (const media of noteToDelete.media) {
              if (media.type === 'image' && media.path) {
                try {
                  // # Create reference to the file and delete it
                  const fileRef = ref(storage, media.path);
                  await deleteObject(fileRef);
                  console.log(`Deleted media file: ${media.path}`);
                } catch (error) {
                  console.error(`Error deleting media file: ${media.path}`, error);
                  mediaDeleteErrors++;
                  // # Continue with note deletion even if media deletion fails
                }
              }
            }
          }
          
          // # Log warning if there were media deletion errors
          if (mediaDeleteErrors > 0) {
            console.warn(`Failed to delete ${mediaDeleteErrors} media files while deleting note ${noteId}`);
          }
          
          // # Delete the note from Firestore
          await updateDoc(notesRef, {
            [`${stepKey}.${chapterKey}.${noteId}`]: deleteField(),
            notesCount: Math.max((notesData.notesCount || 0) - 1, 0) // # Decrement note count, minimum 0
          });
          
          // # Update local state to remove the note
          setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId));
          
          console.log(`Note ${noteId} successfully deleted`);
        } else {
          console.error(`Note ${noteId} not found in path: ${stepKey}.${chapterKey}`);
          alert('Error: Note not found. It may have been already deleted.');
        }
      } else {
        console.error('Notes document not found for user');
        alert('Error: User notes not found. Please try again later.');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteNoteId(null);
      setConfirmNoteId(null);
    }
  };
  
  // # Format display title for all chapters
  const formatDisplayTitle = (title) => {
    if (!title) return '';
    
    // # Replace underscores with spaces
    let formattedTitle = title.replace(/_/g, ' ');
    
    // # Handle case where title is all uppercase with underscores (Step 2 format)
    if (/^[A-Z,&\s]+$/.test(formattedTitle)) {
      // # Convert to title case (first letter of each word uppercase, rest lowercase)
      formattedTitle = formattedTitle
        .split(' ')
        .map(word => {
          // # Keep special characters as is
          if (word === '&') return '&';
          // # Convert to title case
          return word.charAt(0) + word.slice(1).toLowerCase();
        })
        .join(' ');
    } 
    // # Handle lowercase with underscores (Step 1 format)
    else if (/^[a-z_&\s]+$/.test(title)) {
      // # Convert to title case
      formattedTitle = formattedTitle
        .split(' ')
        .map(word => {
          // # Keep special characters as is
          if (word === '&') return '&';
          // # Convert first character to uppercase
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
    }
    
    // # Ensure proper spacing around ampersands and commas
    formattedTitle = formattedTitle
      .replace(/\s*&\s*/g, ' & ')
      .replace(/\s*,\s*/g, ', ');
    
    return formattedTitle;
  };
  
  // # Function to strip HTML/markdown tags for preview
  const stripHtmlTags = (htmlString) => {
    if (!htmlString) return '';
    // # Create a temporary div to hold the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    // # Return just the text content
    return tempDiv.textContent || tempDiv.innerText || '';
  };
  
  // # Create a formatted preview from HTML content
  const renderPreview = (htmlContent, maxLength = 100) => {
    if (!htmlContent) return '';
    
    // # Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // # Process lists to show bullets and numbers
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach(list => {
      const isOrdered = list.tagName === 'OL';
      const items = list.querySelectorAll('li');
      items.forEach((item, index) => {
        const prefix = isOrdered ? `${index + 1}. ` : '• ';
        item.textContent = prefix + item.textContent;
      });
    });
    
    // # Replace common elements with text markers
    const bolds = tempDiv.querySelectorAll('strong, b');
    bolds.forEach(bold => {
      bold.textContent = `*${bold.textContent}*`;
    });
    
    const italics = tempDiv.querySelectorAll('em, i');
    italics.forEach(italic => {
      italic.textContent = `_${italic.textContent}_`;
    });
    
    // # Preserve line breaks
    const paragraphs = tempDiv.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6');
    paragraphs.forEach((p, index) => {
      if (index > 0) {
        p.insertAdjacentHTML('beforebegin', '\n');
      }
    });
    
    // # Extract the text content
    let plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // # Limit to 5 words if text contains more than 5 words
    const words = plainText.split(/\s+/);
    if (words.length > 5) {
      plainText = words.slice(0, 5).join(' ') + '...';
      return plainText;
    }
    
    // # Limit to 3 lines
    const lines = plainText.split('\n');
    if (lines.length > 3) {
      return lines.slice(0, 3).join('\n') + '...';
    }
    
    // # Also check overall length
    if (plainText.length > maxLength) {
      return plainText.substring(0, maxLength) + '...';
    }
    
    return plainText;
  };
  
  // # Format date with validation
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      // # Check if it's a Firestore Timestamp
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleDateString();
      }
      
      // # Handle regular Date objects or timestamps
      const date = new Date(dateValue);
      // # Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };
  
  // # Helper to normalize chapter ID for database queries
  const normalizeChapterId = (id) => {
    // # For database consistency, always convert to lowercase with underscores
    // # Also replace hyphens with underscores
    return id.toLowerCase().replace(/[\s,&-]+/g, '_');
  };
  
  // # Fetch user's notes for this chapter
  useEffect(() => {
    const fetchNotes = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // # Get notes document reference
        const notesRef = doc(db, 'notes', user.uid);
        const notesDoc = await getDoc(notesRef);
        
        if (notesDoc.exists()) {
          // # Format keys for the new nested structure
          const stepKey = `${stepId}_notes`.replace(/[\s-]+/g, '_').toLowerCase();
          const chapterKey = normalizeChapterId(chapterId);
          const notesData = notesDoc.data();
          
          // # Check if the step and chapter maps exist
          if (notesData[stepKey] && notesData[stepKey][chapterKey]) {
            // # Convert map to array with id included for easier rendering
            const chapterNotesMap = notesData[stepKey][chapterKey];
            const notesArray = Object.keys(chapterNotesMap).map(noteId => ({
              id: noteId,
              ...chapterNotesMap[noteId]
            }));
            
            setNotes(notesArray);
          } else {
            setNotes([]);
          }
        } else {
          setNotes([]);
        }
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotes();
  }, [user, stepId, chapterId]);
  
  // # Determine if this is a Step 2 chapter
  const isStep2 = stepId.includes('Step 2');
  
  // # Get chapter icon based on chapter name
  const getChapterIcon = (chapterName) => {
    // # Define chapter mapping for both Step 1 and Step 2 based on my_notes.js
    const step1ChapterMap = {
      'biochemistry': biochemistryIcon,
      'immunology': immunologyIcon,
      'microbiology': infectiousIcon,
      'general_pathology': pathologyIcon,
      'pharmacology': pharmaIcon,
      'public_health_sciences': publicHealthIcon,
      'anatomy_histology': anatomyIcon,
      'cardiovascular': heartIcon,
      'endocrine': endocrineIcon,
      'gastrointestinal': gitIcon,
      'hematology_oncology': bloodIcon,
      'musculoskeletal_connective_tissue': musculoskeletalIcon,
      'dermatology': skinIcon,
      'neurology': cnsIcon,
      'special_senses': eyeIcon,
      'psychiatry': psychiatryIcon,
      'renal': kidneyIcon,
      'reproductive': reproductiveIcon,
      'respiratory': lungsIcon,
      'rapid_review': revisionIcon,
      'sketchy_micro': infectiousIcon
    };

    const step2ChapterMap = {
      'cardiovascular': heartIcon,
      'respiratory_ent': lungsIcon,
      'renal_urology': kidneyIcon,
      'endocrine': endocrineIcon,
      'hematology_oncology': bloodIcon,
      'immunology': immunologyIcon,
      'git': gitIcon,
      'musculoskeletal': musculoskeletalIcon,
      'neurology': cnsIcon,
      'ophthalmology': eyeIcon,
      'psychiatry': psychiatryIcon,
      'dermatology': skinIcon,
      'gynecology_obstetrics': reproductiveIcon,
      'neonate_infant': newbornIcon,
      'genetics': biochemistryIcon,
      'infectious_disease': infectiousIcon,
      'surgery_anesthesia': surgeryIcon,
      'emergency_trauma_toxicology': emergencyIcon,
      'the_well_patient': wellPatientIcon,
      'biostatistics_epidemiology': statisticsIcon,
      'ethics_communication': publicHealthIcon,
      'healthcare_delivery': healthcareIcon,
      'quality_safety': qualityIcon,
      'pharmacology_review': pharmaIcon
    };

    // # Normalize the chapter name for matching
    const normalizedName = chapterName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    // # First try exact match from the appropriate map based on step
    const chapterMap = isStep2 ? step2ChapterMap : step1ChapterMap;
    if (chapterMap[normalizedName]) {
      return chapterMap[normalizedName];
    }
    
    // # If no exact match, try partial match
    for (const [key, value] of Object.entries(chapterMap)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        return value;
      }
    }
    
    // # If still no match, check the other step's map
    const otherMap = isStep2 ? step1ChapterMap : step2ChapterMap;
    for (const [key, value] of Object.entries(otherMap)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        return value;
      }
    }
    
    // # Use these common keywords for further matching
    const iconKeywords = {
      'heart': heartIcon,
      'cardio': heartIcon,
      'lung': lungsIcon,
      'resp': lungsIcon,
      'kidney': kidneyIcon,
      'renal': kidneyIcon,
      'endocrine': endocrineIcon,
      'blood': bloodIcon,
      'hemat': bloodIcon,
      'oncol': bloodIcon,
      'immun': immunologyIcon,
      'gastro': gitIcon,
      'intestin': gitIcon,
      'muscle': musculoskeletalIcon,
      'skeletal': musculoskeletalIcon,
      'connect': musculoskeletalIcon,
      'neuro': cnsIcon,
      'eye': eyeIcon,
      'ophthal': eyeIcon,
      'psych': psychiatryIcon,
      'mental': psychiatryIcon,
      'skin': skinIcon,
      'derma': skinIcon,
      'repro': reproductiveIcon,
      'gynec': reproductiveIcon,
      'obstet': reproductiveIcon,
      'infant': newbornIcon,
      'neonate': newbornIcon,
      'baby': newbornIcon,
      'gene': biochemistryIcon,
      'infect': infectiousIcon,
      'micro': infectiousIcon,
      'surg': surgeryIcon,
      'anesth': surgeryIcon,
      'emerg': emergencyIcon,
      'trauma': emergencyIcon,
      'toxic': emergencyIcon,
      'well': wellPatientIcon,
      'prevent': wellPatientIcon,
      'stat': statisticsIcon,
      'epidem': statisticsIcon,
      'ethic': publicHealthIcon,
      'commun': publicHealthIcon,
      'health': healthcareIcon,
      'quality': qualityIcon,
      'safety': qualityIcon,
      'pharma': pharmaIcon
    };
    
    // # Check for keyword matches
    for (const [key, icon] of Object.entries(iconKeywords)) {
      if (normalizedName.includes(key)) {
        return icon;
      }
    }
    
    // # Default icon if no match found
    return isStep2 ? stepTwoIcon : revisionIcon;
  };

  // # Custom confirmation dialog component
  const ConfirmDeleteDialog = ({ isOpen, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    
    return (
      <div className="cn_confirm_overlay" onClick={onCancel}>
        <div className="cn_confirm_dialog" onClick={(e) => e.stopPropagation()}>
          <h3 className="cn_confirm_title">Delete Note</h3>
          <p className="cn_confirm_message">
            Are you sure you want to delete this note? This action cannot be undone.
          </p>
          <div className="cn_confirm_buttons">
            <button className="cn_confirm_cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="cn_confirm_delete" onClick={() => onConfirm(confirmNoteId)}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`chp_nts_note_chapter_container ${isDarkMode ? 'chp_nts_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <div className="chp_nts_note_chapter_main">
        {/* Chapter Title Box */}
        <div className="chp_nts_note_chapter_title_box">
          <div className="chp_nts_note_chapter_icon">
            <img src={getChapterIcon(chapterId)} alt={chapterId} />
          </div>
          <div className="chp_nts_note_chapter_info">
            <h2>{formatDisplayTitle(chapterId)}</h2>
            <p>{stepId} Notes</p>
          </div>
          <div className="chp_nts_note_title_buttons">
            <button 
              onClick={handleCreateNote} 
              className="chp_nts_note_create_button"
            >
              <FaPlus /> Create Note
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="chp_nts_note_loading">Loading your notes...</div>
        ) : notes.length > 0 ? (
          <div className="chp_nts_note_list">
            {notes.map(note => (
              <div key={note.id} className="chp_nts_note_item">
                <div className="chp_nts_note_item_header">
                  <h3>{note.title}</h3>
                  <span className="chp_nts_note_item_date">
                    {formatDate(note.updatedAt)}
                  </span>
                </div>
                <div className="chp_nts_note_item_preview">
                  {/* # Use renderPreview for text content */}
                  {renderPreview(note.content)}
                </div>
                <div className="chp_nts_note_actions">
                  <button 
                    className="chp_nts_note_view_button"
                    onClick={() => {
                      const isAdmin = window.location.pathname.startsWith('/admin');
                      const basePath = isAdmin ? '/admin/notes' : '/notes';
                      const formattedChapterId = formatChapterId(chapterId);
                      navigate(`${basePath}/${stepId}/${formattedChapterId}/edit/${note.id}`);
                    }}
                  >
                    View Note
                  </button>
                  <button 
                    className="chp_nts_note_delete_button"
                    onClick={() => showDeleteConfirmation(note.id)}
                    disabled={deleting && deleteNoteId === note.id}
                  >
                    {deleting && deleteNoteId === note.id ? (
                      'Deleting...'
                    ) : (
                      <>
                        <FaTrash /> Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="chp_nts_note_empty">
            <p>You don't have any notes for this chapter yet.</p>
            <p>Click the "Create Note" button to add your first note!</p>
          </div>
        )}
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmDeleteDialog 
        isOpen={showConfirmDialog}
        onConfirm={handleDeleteNote}
        onCancel={handleCancelDelete}
      />
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default ChapterNotes; 